import {
  Circle,
  FeatureGroup,
  MapContainer,
  Marker,
  Polygon,
  Popup,
  TileLayer,
} from "react-leaflet";
import { useContext, useEffect, useRef, useState } from "react";
import { Icon, Map } from "leaflet";
import { User } from "./StaffGPSUsers.route";
import { useCommanData } from "../../../contexts/commanData.context";
import BackButton from "../../../components/modals/AddAssetModal/BackButton/BackButton";
import i18next from "i18next";
import LoadingData from "../../../components/loadingData/loadingData.component";
import useOsmProviders from "./useOsmProviders";
import { useParams, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../contexts/user.context";
import MarkerClusterGroup from "react-leaflet-cluster";
import { EditControl } from "react-leaflet-draw";
import { LocationMarker } from "./LocationMarker";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { Get, Post, handleError } from "../../../utils/fetch.util";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Site } from "../floors/floors.route";
import { GeneralObj, SiteType } from "../../../types/company.types";
import { CiSearch } from "react-icons/ci";
import { FaExclamation } from "react-icons/fa6";
import { IoLocationOutline } from "react-icons/io5";
import { MdClear } from "react-icons/md";

type Coordinate = {
  latitude: number;
  longitude: number;
};

type PlaceSuggestion = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  importance: number;
};

type PlaceBoundary = {
  coordinates: [number, number][];
  name: string;
  type: string;
};

export default function OsmMap({
  users,
  getLastSeen,
  selectedSite,
}: {
  users: User[];
  getLastSeen: any;
  selectedSite?: any;
}) {
  const [, setSite] = useState<Site | SiteType | GeneralObj>({});
  const [siteLocations, setSiteLocations] = useState<any>({});
  const [searchValue, setSearchValue] = useState<string>("");
  const [placeSearchValue, setPlaceSearchValue] = useState<string>("");
  const [placeSuggestions, setPlaceSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isSearchingPlaces, setIsSearchingPlaces] = useState<boolean>(false);
  const [showPlaceSuggestions, setShowPlaceSuggestions] = useState<boolean>(false);
  const [selectedPlaceBoundary, setSelectedPlaceBoundary] = useState<PlaceBoundary | null>(null);
  const mapRef = useRef<Map>(null);
  const osm = useOsmProviders();
  const { sectorId, siteId, status } = useParams();
  const [searchParams] = useSearchParams();
  const { API_LINK } = useCommanData();
  const { getValidToken } = useContext(UserContext);
  const { userData } = useContext(UserContext);
  const { t } = useTranslation();
  const [userId, setUserId] = useState<number>(
    Number(searchParams.get("userId"))
  );
  const copmanyLat = userData[0].gpsLocation
    ?.slice(30, userData[0].gpsLocation.length)
    ?.split(",")[0];
  const companyLng = userData[0].gpsLocation
    ?.slice(30, userData[0].gpsLocation.length)
    ?.split(",")[1];

  function isArabicString(inputString: string): boolean {
    const arabicRegex = /^[\u0600-\u06FF\s]+$/;
    return arabicRegex.test(inputString);
  }

  const setSiteLocation = async (
    latlangs: { latitude: number; longitude: number }[]
  ) => {
    const res = await Post(
      "sites/set-site-locations",
      { site_locations: JSON.stringify(latlangs), site_id: selectedSite.site_id, asdc_client_id: Number(searchParams.get("client_id")) },
      getValidToken()
    );

    if (res.error || !res.data) {
      return handleError(res);
    }

    toast.success("Saved Site Location");
  };

  const savePlaceBoundaryAsSiteLocation = (boundaryData?: PlaceBoundary) => {
    // Use passed boundary data or fallback to state
    const boundary = boundaryData || selectedPlaceBoundary;
    console.log(boundary, selectedSite);

    if (boundary && selectedSite) {
      // Hide any remaining suggestions
      setShowPlaceSuggestions(false);
      setPlaceSuggestions([]);

      // Convert place boundary coordinates to the required format
      const siteLocation = boundary.coordinates.map(coord => ({
        latitude: coord[0],
        longitude: coord[1]
      }));

      // Update local state immediately
      setSiteLocations(boundary.coordinates);

      // Save to server
      setSiteLocation(siteLocation);

      // Clear the place boundary since it's now the site boundary
      setSelectedPlaceBoundary(null);

      toast.success(t("place_boundary_applied_as_site_location") || "Place boundary applied as site location!");
    }
  };

  function convertCoordinates(coordinates: Coordinate[]): [number, number][] {
    return coordinates.map(({ latitude, longitude }) => [latitude, longitude]);
  }

  const flyToUserLocation = (userId: number) => {
    const user = users.filter((u) => {
      return u.gpsLocation && u.asdc_client_user_id === userId;
    })[0];

    const lat =
      user?.gpsLocation &&
      Number(
        user?.gpsLocation?.slice(30, user.gpsLocation.length)?.split(",")[0]
      );
    const lng =
      user?.gpsLocation &&
      Number(
        user?.gpsLocation?.slice(30, user.gpsLocation.length)?.split(",")[1]
      );

    mapRef.current?.flyTo([Number(lat || 30), Number(lng || 32)], 18, {
      animate: true,
    });
  };

  const searchPlaces = async (query: string) => {
    if (query.length < 3) {
      setPlaceSuggestions([]);
      setShowPlaceSuggestions(false);
      return;
    }

    setIsSearchingPlaces(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5&addressdetails=1&extratags=1`
      );
      const data: PlaceSuggestion[] = await response.json();
      setPlaceSuggestions(data);
      setShowPlaceSuggestions(true);
    } catch (error) {
      console.error("Error searching places:", error);
      setPlaceSuggestions([]);
    } finally {
      setIsSearchingPlaces(false);
    }
  };

  const fetchPlaceBoundary = async (placeId: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/details.php?place_id=${placeId}&format=json&polygon_geojson=1`
      );
      const data = await response.json();

      if (data.geometry && data.geometry.coordinates) {
        let coordinates: [number, number][] = [];

        // Handle different geometry types
        if (data.geometry.type === 'Polygon') {
          // For polygons, take the outer ring (first array)
          coordinates = data.geometry.coordinates[0].map((coord: [number, number]) => [coord[1], coord[0]]);
        } else if (data.geometry.type === 'MultiPolygon') {
          // For multipolygons, take the first polygon's outer ring
          coordinates = data.geometry.coordinates[0][0].map((coord: [number, number]) => [coord[1], coord[0]]);
        } else if (data.geometry.type === 'LineString') {
          // For linestrings, use coordinates directly
          coordinates = data.geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]);
        }

        if (coordinates.length > 0) {
          const placeBoundary = {
            coordinates,
            name: data.localname || data.names?.name || 'Selected Place',
            type: data.geometry.type
          };

          setSelectedPlaceBoundary(placeBoundary);

          // Fit map to boundary
          if (coordinates.length > 1) {
            const bounds = coordinates.reduce(
              (bounds, coord) => [
                [Math.min(bounds[0][0], coord[0]), Math.min(bounds[0][1], coord[1])],
                [Math.max(bounds[1][0], coord[0]), Math.max(bounds[1][1], coord[1])]
              ],
              [[coordinates[0][0], coordinates[0][1]], [coordinates[0][0], coordinates[0][1]]]
            );
            mapRef.current?.fitBounds(bounds as [[number, number], [number, number]], { padding: [20, 20] });
          }

          // Show suggestion toast only if we have a selected site
          if (selectedSite) {
            const toastId = `place-boundary-${Date.now()}`;

            toast(
              <div className="flex flex-col gap-3 p-2">
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500 mt-1 flex-shrink-0"></div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">
                      {t("use_place_boundary") || "Use Place Boundary?"}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {t("place_boundary_suggestion") || `Use "${placeBoundary.name}" boundary as site coordinates?`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toast.dismiss(toastId);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                  >
                    {t("cancel") || "Cancel"}
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      savePlaceBoundaryAsSiteLocation(placeBoundary);
                      toast.dismiss(toastId);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                  >
                    {t("use_boundary") || "Use Boundary"}
                  </button>
                </div>
              </div>,
              {
                toastId,
                position: "top-right",
                autoClose: 8000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: false,
                style: {
                  right: '100px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  position: 'fixed',
                  maxWidth: '320px',
                  borderLeft: '4px solid #ff6b35'
                }
              }
            );
          }
        }
      }
    } catch (error) {
      console.error('Error fetching place boundary:', error);
    }
  };

  const flyToPlace = (place: PlaceSuggestion) => {
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);

    // Clear previous boundary
    setSelectedPlaceBoundary(null);

    // Hide suggestions immediately
    setShowPlaceSuggestions(false);
    setPlaceSuggestions([]);

    // Fly to location
    mapRef.current?.flyTo([lat, lng], 15, {
      animate: true,
    });

    // Fetch and display boundary
    fetchPlaceBoundary(place.place_id);

    setPlaceSearchValue(place.display_name);
  };

  // Debounce place search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (placeSearchValue) {
        searchPlaces(placeSearchValue);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [placeSearchValue]);

  useEffect(() => {
    if (userId) {
      flyToUserLocation(userId);
    }
  }, [userId]);

  useEffect(() => {
    const getSite = async () => {
      const res = await Get(
        `sites/get-sector-site?asdc_client_sector_id=${sectorId}&asdc_client_site_id=${siteId}`,
        getValidToken()
      );

      if (res.error || !res.data) return;

      const coordinates = convertCoordinates(res.data.site_locations);

      setSite({
        ...res.data,
        site_locations: coordinates,
      });
      setSiteLocations(coordinates);

      // Fly to site boundaries if they exist
      if (coordinates && coordinates.length > 0 && mapRef.current) {
        if (coordinates.length > 1) {
          // Calculate bounds for all coordinates
          const bounds = coordinates.reduce(
            (bounds, coord) => [
              [Math.min(bounds[0][0], coord[0]), Math.min(bounds[0][1], coord[1])],
              [Math.max(bounds[1][0], coord[0]), Math.max(bounds[1][1], coord[1])]
            ],
            [[coordinates[0][0], coordinates[0][1]], [coordinates[0][0], coordinates[0][1]]]
          );

          // Fit bounds with padding to show the entire site boundary
          mapRef.current.fitBounds(bounds as [[number, number], [number, number]], {
            padding: [50, 50],
            animate: true,
            duration: 1
          });
        } else if (coordinates.length === 1) {
          // If only one coordinate, fly to it
          mapRef.current.flyTo([coordinates[0][0], coordinates[0][1]], 15, {
            animate: true,
          });
        }
      }
    };

    siteId && getSite();
  }, [sectorId, siteId, getValidToken]);

  // Handle selectedSite from AdminSites
  useEffect(() => {
    // Reset/clear previous site data when site changes or becomes null
    setSiteLocations([]);
    setSelectedPlaceBoundary(null);
    setPlaceSearchValue("");
    setShowPlaceSuggestions(false);

    setTimeout(() => {
      if (selectedSite) {
        if (selectedSite.site_locations) {
          try {
            const locations = typeof selectedSite.site_locations === 'string'
              ? JSON.parse(selectedSite.site_locations)
              : selectedSite.site_locations;

            if (locations && locations.length > 0) {
              const coordinates = convertCoordinates(locations);
              setSiteLocations(coordinates);

              // Fit map to show all site boundaries
              if (coordinates.length > 1 && mapRef.current) {
                // Calculate bounds for all coordinates
                const bounds = coordinates.reduce(
                  (bounds, coord) => [
                    [Math.min(bounds[0][0], coord[0]), Math.min(bounds[0][1], coord[1])],
                    [Math.max(bounds[1][0], coord[0]), Math.max(bounds[1][1], coord[1])]
                  ],
                  [[coordinates[0][0], coordinates[0][1]], [coordinates[0][0], coordinates[0][1]]]
                );

                // Fit bounds with padding to show the entire site boundary
                mapRef.current.fitBounds(bounds as [[number, number], [number, number]], {
                  padding: [50, 50],
                  animate: true,
                  duration: 1
                });
              } else if (coordinates.length === 1 && mapRef.current) {
                // If only one coordinate, fly to it
                mapRef.current.flyTo([coordinates[0][0], coordinates[0][1]], 15, {
                  animate: true,
                });
              }
            }
          } catch (error) {
            console.error('Error parsing site locations:', error);
          }
        } else {
          // If no existing locations, fly to a default location or company location
          if (mapRef.current && copmanyLat && companyLng) {
            mapRef.current.flyTo([Number(copmanyLat), Number(companyLng)], 10, {
              animate: true,
            });
          }
        }
      } else {
        // If no site is selected, fly back to company location
        if (mapRef.current && copmanyLat && companyLng) {
          mapRef.current.flyTo([Number(copmanyLat), Number(companyLng)], 10, {
            animate: true,
          });
        }
      }
    }, 500);
  }, [selectedSite, copmanyLat, companyLng]);

  if (!mapRef.current === null) {
    return <LoadingData />;
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-5 mt-12">
      <div className="w-full flex items-center justify-between">
        <BackButton position="self-start ml-16" />
        <div className="flex items-center gap-4">
          {/* Place Search Input */}
          <div className="flex items-center w-64 relative">
            <label className="-me-8 z-50 text-xl">
              <IoLocationOutline />
            </label>
            <input
              type="search"
              placeholder="Search places..."
              className="ps-10 py-2 px-4 w-full rounded-md outline outline-primary"
              onChange={(e) => {
                setPlaceSearchValue(e.target.value);
                if (e.target.value.length === 0) {
                  setShowPlaceSuggestions(false);
                }
              }}
              value={placeSearchValue}
              onFocus={() => {
                if (placeSuggestions.length > 0) {
                  setShowPlaceSuggestions(true);
                }
              }}
              onBlur={(e) => {
                // Only hide if not clicking on suggestions or toast
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (!relatedTarget || (!relatedTarget.closest('.place-suggestions') && !relatedTarget.closest('.Toastify__toast'))) {
                  setTimeout(() => setShowPlaceSuggestions(false), 100);
                }
              }}
            />
            {isSearchingPlaces && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              </div>
            )}
            {selectedPlaceBoundary && !isSearchingPlaces && (
              <button
                onClick={() => {
                  setSelectedPlaceBoundary(null);
                  setPlaceSearchValue("");
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear place boundary"
              >
                <MdClear size={16} />
              </button>
            )}
            {/* Place Suggestions Dropdown */}
            <div
              className={`place-suggestions absolute top-12 left-0 w-full duration-300 z-[10000] bg-white border border-gray-200 rounded-md shadow-lg ${showPlaceSuggestions && placeSuggestions.length > 0
                ? "max-h-60 overflow-y-auto"
                : "h-0 overflow-hidden"
                }`}
            >
              {placeSuggestions.map((place) => (
                <div
                  key={place.place_id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                  onClick={() => flyToPlace(place)}
                >
                  <IoLocationOutline className="text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {place.display_name.split(',')[0]}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {place.display_name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* User Search Input */}
          <div className="flex items-center w-64 relative">
            {users.length > 0 && <>
              <label className="-me-8 z-50 text-xl">
                <CiSearch />
              </label>
              <input
                type="search"
                placeholder="Search users..."
                className="ps-10 py-2 px-4 w-full rounded-md outline outline-primary"
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                value={searchValue}
              /></>}
            <div
              className={`absolute top-12 left-0 w-full duration-300 z-[10000] bg-white border border-gray-200 rounded-md shadow-lg ${searchValue.length > 0 ? "max-h-60 overflow-y-auto" : "h-0 overflow-hidden"
                }`}
            >
              {users
                ?.filter((user) =>
                  user.asdc_client_user_name
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
                )
                .map((user, index: number) => {
                  return (
                    <div
                      key={`${user.asdc_client_user_name.concat(
                        index.toString()
                      )}`}
                      className="flex items-center gap-4 p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => {
                        setUserId(user.asdc_client_user_id);
                        setSearchValue("");
                      }}
                    >
                      <img
                        src={`${API_LINK}${user.profile_pic}`}
                        alt="user"
                        className="w-10 h-10 rounded-full"
                      />
                      <div
                        className={`capitalize font-bold ${isArabicString(user.asdc_client_user_name)
                          ? i18next.language === "en"
                            ? "text-end"
                            : "text-start"
                          : i18next.language === "en"
                            ? "text-start"
                            : "text-end"
                          }`}
                      >
                        <h3 className="text-sm font-medium text-gray-900">{user.asdc_client_user_name}</h3>
                        <b className="block text-xs text-gray-500">{user.asdc_client_user_mobile}</b>
                        <b
                          className={`text-xs ${window.location.href.includes("offline")
                            ? "text-red-500"
                            : "text-green-500"
                            }`}
                        >
                          {window.location.href.includes("offline")
                            ? `Offline ${getLastSeen(user.updatedAt)}`
                            : "Online"}
                        </b>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
      <div></div>
      <div className="w-full max-w-[1100px] mx-auto">
        <span
          className="text-end w-[25px] h-[25px] rounded-full text-white bg-black flex items-center justify-center ml-auto cursor-pointer custom-desc before:content-[attr(data-desc)]"
          data-desc="Set shapes by clicking to draw polygons, circles, or rectangles. Delete shapes by selecting and pressing delete/backspace."
        >
          <FaExclamation />
        </span>
      </div>
      <MapContainer
        center={[copmanyLat || 30, companyLng || 32]}
        zoom={11}
        ref={mapRef}
        style={{ overflow: "hidden" }}
      >
        {!status && status !== "offline" && status !== "online" && (
          <FeatureGroup>
            <EditControl
              position="topright"
              onCreated={(e) => {
                // Clear previous polygons/coordinates before creating new one
                setSiteLocations([]);
                setSelectedPlaceBoundary(null);

                const latlngs = e.layer.getLatLngs()[0];
                latlngs.push(latlngs[0]);
                const siteLocation = latlngs.map(
                  (latlng: { lat: number; lng: number }) => ({
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                  })
                );

                // Update the local state immediately to show the new polygon
                const coordinates = convertCoordinates(siteLocation);
                setSiteLocations(coordinates);

                setSiteLocation(siteLocation);
              }}
              onEdited={(e) => {
                // Handle polygon edits
                const layers = e.layers;
                layers.eachLayer((layer: any) => {
                  if (layer.getLatLngs) {
                    const latlngs = layer.getLatLngs()[0];
                    latlngs.push(latlngs[0]);
                    const siteLocation = latlngs.map(
                      (latlng: { lat: number; lng: number }) => ({
                        latitude: latlng.lat,
                        longitude: latlng.lng,
                      })
                    );

                    // Update the local state with edited coordinates
                    const coordinates = convertCoordinates(siteLocation);
                    setSiteLocations(coordinates);

                    setSiteLocation(siteLocation);
                  }
                });
              }}
              onDeleted={() => {
                setSiteLocation([]);
                setSiteLocations([]);
                setSelectedPlaceBoundary(null);
              }}
              draw={{
                rectangle: false,
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
              }}
              edit={{
                remove: false,
                edit: false,
              }}
            // onDeleteStop={(e) => setSiteLocation([])}
            />
            <Circle center={[51.51, -0.06]} radius={200} />
          </FeatureGroup>
        )}
        <TileLayer
          attribution={osm.maptiler.attribution}
          url={osm.maptiler.url}
        />
        {siteLocations && <Polygon positions={siteLocations} />}
        {/* Place Boundary Highlight */}
        {selectedPlaceBoundary && (
          <Polygon
            positions={selectedPlaceBoundary.coordinates}
            pathOptions={{
              color: '#ff6b35',
              fillColor: '#ff6b35',
              fillOpacity: 0.2,
              weight: 3,
              opacity: 0.8,
            }}
          >
            <Popup>
              <div className="font-bold">
                <h3 className="text-sm font-medium text-gray-900">
                  {selectedPlaceBoundary.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedPlaceBoundary.type} boundary
                </p>
              </div>
            </Popup>
          </Polygon>
        )}
        <MarkerClusterGroup chunkedLoading>
          {users?.map((user, index: number) => {
            const lat =
              user?.gpsLocation &&
              Number(
                user?.gpsLocation
                  ?.slice(30, user.gpsLocation.length)
                  ?.split(",")[0]
              );

            const lng =
              user?.gpsLocation &&
              Number(
                user?.gpsLocation
                  ?.slice(30, user.gpsLocation.length)
                  ?.split(",")[1]
              );

            return (
              <Marker
                key={`${user.asdc_client_user_name.concat(index.toString())}`}
                position={[lat || 30, lng || 32]}
                icon={
                  new Icon({
                    iconUrl: `${API_LINK}${user.profile_pic}`,
                    iconSize: [35, 45],
                    iconAnchor: [17.5, 46], // [left/right, top/bottom]
                    popupAnchor: [0, -46], // [left/right, top/bottom]
                    className:
                      "!relative !rounded-full !w-12 !h-12 !aspect-square !object-cover",
                  })
                }
                eventHandlers={{
                  mouseover: (e) => e.target.openPopup(),
                  mouseout: (e) => e.target.closePopup(),
                  click: () => {
                    // mapRef?.current?.flyTo([Number(lat), Number(lng)], 18, {
                    //   animate: true,
                    // });
                    setUserId(user.asdc_client_user_id);
                  },
                }}
              >
                <Popup>
                  <div
                    className={`capitalize font-bold ${isArabicString(user.asdc_client_user_name)
                      ? i18next.language === "en"
                        ? "text-end"
                        : "text-start"
                      : i18next.language === "en"
                        ? "text-start"
                        : "text-end"
                      }`}
                  >
                    <h3>{user.asdc_client_user_name}</h3>
                    <b className="block my-3">{user.asdc_client_user_mobile}</b>
                    <b
                      className={`${window.location.href.includes("offline")
                        ? "text-red-500"
                        : "text-green-500"
                        }`}
                    >
                      {window.location.href.includes("offline")
                        ? `Offline ${getLastSeen(user.updatedAt)}`
                        : "Online"}
                    </b>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
        <LocationMarker />
      </MapContainer>
      <div className="flex items-center gap-4">
        <button
          onClick={() => osm.setMaptilerURL(osm.streets)}
          className={`${osm.maptilerURL.includes("street") && "bg-primary"
            } outline outline-primary text-text p-3 px-4 rounded-md hover:bg-primary transition`}
        >
          Streets
        </button>
        <button
          onClick={() => osm.setMaptilerURL(osm.lbmDark)}
          className={`${osm.maptilerURL.includes("lbm-dark") && "bg-primary"
            } outline outline-primary text-text p-3 px-4 rounded-md hover:bg-primary transition`}
        >
          LBM Dark
        </button>
        <button
          onClick={() => osm.setMaptilerURL(osm.satellite)}
          className={`${osm.maptilerURL.includes("satellite") && "bg-primary"
            } outline outline-primary text-text p-3 px-4 rounded-md hover:bg-primary transition`}
        >
          Satellite
        </button>
      </div>
    </div>
  );
}
